import { useState } from 'react';
import { motion } from 'framer-motion';
import { apiFetch, ApiError } from '@/api/client';
import type { FeishuLoginUrl } from '@/api/types';
import { MaterialIcon } from '@/components/MaterialIcon';
import { useToast } from '@/components/Toast';
import { cn } from '@/utils/classnames';

/**
 * Screen 1 — visual structure mirrors docs/frontend/cbwee_identity_fabric_2/code.html.
 *
 * Backend contract:
 *   GET /auth/feishu/login → { url: string, state: string }   (src/feishu.rs:88)
 * The button stays Feishu-only (no password / SMS / other IdP options).
 */
export function LoginPage() {
  const toast = useToast();
  const [pending, setPending] = useState(false);

  async function onLogin() {
    setPending(true);
    try {
      const json = await apiFetch<FeishuLoginUrl>('/auth/feishu/login');
      window.location.assign(json.url);
    } catch (err) {
      const message =
        err instanceof ApiError && err.status > 0
          ? `无法获取飞书授权地址（HTTP ${err.status}），请检查后端是否在 8080 端口运行`
          : '无法获取飞书授权地址，请检查后端是否在 8080 端口运行';
      toast.push({ message, variant: 'error' });
      setPending(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-container-padding font-body-md text-on-background">
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="flex w-full max-w-[480px] flex-col items-center rounded-xl border border-outline-variant bg-surface-container-lowest p-8 text-center shadow-sm"
      >
        <div className="mb-stack-md">
          <div className="mx-auto mb-stack-sm flex h-16 w-16 items-center justify-center rounded-full border border-outline-variant bg-surface-container-high">
            <MaterialIcon name="security" size={32} weight={500} filled className="text-primary" />
          </div>
          <h1 className="mb-unit font-headline-lg text-headline-lg text-on-surface">
            Cbwee Identity Fabric
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            飞书身份源 · OIDC Provider · 平台级访问控制
          </p>
        </div>

        <div className="mt-stack-md flex w-full flex-col gap-stack-md">
          <button
            type="button"
            disabled={pending}
            onClick={onLogin}
            className={cn(
              'flex h-12 w-full items-center justify-center gap-stack-sm rounded',
              'bg-primary-container font-body-md text-body-md text-on-primary',
              'transition-colors hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-70',
            )}
          >
            {pending ? (
              <MaterialIcon name="progress_activity" size={20} className="animate-spin" />
            ) : (
              <MaterialIcon name="login" size={20} filled />
            )}
            {pending ? '正在跳转飞书授权…' : '使用飞书登录'}
          </button>

          <div className="flex w-full flex-col gap-unit rounded-lg border border-outline-variant bg-surface-container-low p-stack-sm text-left font-code-md text-code-md text-on-surface-variant">
            <div className="flex items-center gap-unit text-primary">
              <MaterialIcon name="swap_horiz" size={16} />
              <span className="font-label-caps text-label-caps uppercase">AUTHENTICATION FLOW</span>
            </div>
            <div className="overflow-x-auto whitespace-nowrap pb-unit">
              <span className="text-on-surface">/auth/feishu/login</span>
              <span className="mx-1 text-outline">-&gt;</span>
              <span className="text-secondary">飞书授权</span>
              <span className="mx-1 text-outline">-&gt;</span>
              <span className="text-on-surface">/auth/feishu/callback</span>
            </div>
          </div>
        </div>

        <div className="mt-stack-md flex w-full items-start gap-stack-sm border-t border-outline-variant pt-stack-md text-left">
          <span className="mt-[2px] text-outline">
            <MaterialIcon name="info" size={18} />
          </span>
          <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
            仅使用飞书身份；业务资源权限由各业务系统自行判断。
          </p>
        </div>
      </motion.section>
    </main>
  );
}
